import Box from '@mui/material/Box';
import {
  rankWith,
  LayoutProps,
  UISchemaElement,
  isLayout,
  uiTypeIs,
} from '@jsonforms/core';
import { withJsonFormsLayoutProps } from '@jsonforms/react';
import { JsonFormsDispatch } from '@jsonforms/react';
import { DENSITY_PRESETS, Density, FORM_CONTAINER_SX, sectionSpanSx } from '../JsonFormsTheme';

/** Whether every control in a section is declared narrow (a value object's block counts through). */
const holdsOnlyNarrowControls = (element: UISchemaElement): boolean => {
  if (element.type === 'Control') return element.options?.narrow === true;
  const children = (element as UISchemaElement & { elements?: UISchemaElement[] }).elements ?? [];
  return children.length > 0 && children.every(holdsOnlyNarrowControls);
};

/**
 * Tester function to determine if this renderer should be used for GridLayout
 */
export const gridLayoutRendererTester = rankWith(
  1, // Lower priority than standard layouts, but higher than default
  uiTypeIs('GridLayout')
);

/**
 * GridLayout renderer component
 *
 * Renders a 12-column CSS grid of sections. Child Groups/Controls can specify gridColumn options
 * (e.g. "span 6") to control width; the width actually used steps down responsively with the form
 * container (see sectionSpanSx), so sections stop sitting side by side once the form column is too
 * narrow for them — a drawer opening beside the form, a peek panel, a phone.
 * Reads density/variant from uischema options and propagates to child Groups.
 */
export const GridLayoutRendererComponent = ({ uischema, schema, path, renderers, cells }: LayoutProps) => {
  if (!isLayout(uischema)) {
    return null;
  }

  // Read density/variant from uischema options
  const density: Density = ((uischema as any).options?.density as Density) || 'compact';
  const variant = (uischema as any).options?.variant || 'standard';
  const d = DENSITY_PRESETS[density];

  // Helper to extract gridColumn from a UI schema element
  const getGridColumn = (element: any): number => {
    // Check options.gridColumn on any element type (Control or Group)
    if (element.options?.gridColumn) {
      const match = element.options.gridColumn.match(/span\s+(\d+)/);
      if (match) {
        return parseInt(match[1], 10);
      }
    }
    return 12; // Default to full width
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: d.gridGap,
        alignItems: 'start',
        width: '100%',
        // The box the sections' @container rules measure: the width the form was actually given,
        // whatever the viewport says. Declared here so the renderer is self-sufficient — no host
        // has to remember to mark its content region.
        ...FORM_CONTAINER_SX,
      }}
    >
      {uischema.elements?.map((child, index) => {
        const spanValue = getGridColumn(child);
        const gridSize = Math.min(12, Math.max(1, spanValue));

        // Propagate density/variant to child Groups so GridGroupRenderer can use them
        const enhancedChild = child.type === 'Group'
          ? { ...child, options: { ...child.options, density, variant } }
          : child;

        return (
          <Box
            key={`${path}-${index}`}
            sx={{
              gridColumn: `span ${gridSize}`,
              minWidth: 0,
              // Container steps come last so they override the configured span when the form
              // area — not the viewport — is what is narrow.
              ...sectionSpanSx(gridSize, holdsOnlyNarrowControls(child)),
            }}
          >
            <JsonFormsDispatch
              uischema={enhancedChild}
              schema={schema}
              path={path}
              renderers={renderers}
              cells={cells}
            />
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * GridLayout renderer entry for JSON Forms
 */
export const gridLayoutRendererEntry = {
  tester: gridLayoutRendererTester,
  renderer: withJsonFormsLayoutProps(GridLayoutRendererComponent),
};
